import {yupResolver} from '@hookform/resolvers/yup';
import {Box, TextField, Tooltip, FormControl, InputLabel, Select, MenuItem} from '@mui/material';
import {DatePicker, LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns';
import {enUS, es} from 'date-fns/locale';
import {DateTime} from 'luxon';
import React, {useEffect} from 'react';
import {Controller, useForm, useWatch} from 'react-hook-form';
import * as yup from 'yup';
import {UserType} from '@/components/user/interface';
import {AppDispatch, RootState} from '@/config/store';
import {IFormBillingStatement} from '@components/billingStatement/interface';
import {
    fetchGetSocieties,
    updateFiltersStatus,
} from '@components/billingStatement/redux/reducers/actions/SocietyActions';
import {Search} from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import ErrorToast from '@/components/auth/componenents/ErrorToast';
export const BillingStatementFilters: React.FC = () => {
    const user = useSelector((state: RootState) => state.logins.user);
    const userLang = localStorage.getItem('user-lang') || 'es';
    const lang = userLang === 'es' ? es : enUS;
    const {t} = useTranslation();
    const dispatch: AppDispatch = useDispatch();
    const token = useSelector((state: RootState) => state.logins.accessToken);
    const limit = useSelector((state: RootState) => state.billingStatement.societiesLimit);
    const page = useSelector((state: RootState) => state.billingStatement.societiesPage);
    const schemaFormModal = yup.object({
        id: yup.string(),
        societyName: yup.string(),
        supplier: yup.string(),
        dateInit: yup.date(),
        dateEnd: yup.date(),
        status: yup.string(),
    });

    const {handleSubmit, control} = useForm<IFormBillingStatement>({
        resolver: yupResolver(schemaFormModal),
        defaultValues: {
            status: 'ALL',
        },
    });

    const currentStatus = useWatch({
        control,
        name: 'status',
        defaultValue: 'ALL',
    });

    useEffect(() => {
        if (currentStatus) {
            dispatch(updateFiltersStatus(currentStatus));
        }
    }, [currentStatus, dispatch]);

    const checkDateRange = (info: IFormBillingStatement) => {
        const start = DateTime.fromJSDate(info.dateInit).startOf('day');
        const end = DateTime.fromJSDate(info.dateEnd).startOf('day');
        const status = info.status || 'ALL';

        // Si el estado es ALL, máximo 1 mes
        if (status === 'ALL') {
            const differenceInMonths = end.diff(start, 'months').months;
            return differenceInMonths > 1;
        }

        // Si el estado es SHD o PAY, máximo 1 año
        if (status === 'SHD' || status === 'PAY') {
            const differenceInYears = end.diff(start, 'years').years;
            return differenceInYears > 1;
        }

        // Por defecto, máximo 1 mes
        const differenceInMonths = end.diff(start, 'months').months;
        return differenceInMonths > 1;
    };

    const onSubmit = (info: IFormBillingStatement) => {
        const status = info.status || 'ALL';
        const exceedsRange = checkDateRange(info);

        if (exceedsRange) {
            if (status === 'ALL') {
                ErrorToast({title: t('range_month_exceed_month')});
            } else {
                ErrorToast({title: t('range_year_exceed_year')});
            }
            return;
        }
        dispatch(
            fetchGetSocieties(token, 1, limit, {
                ...info,
                dateInit: DateTime.fromJSDate(info.dateInit).toFormat('yyyy-MM-dd'),
                dateEnd: DateTime.fromJSDate(info.dateEnd).toFormat('yyyy-MM-dd'),
            })
        );
    };

    useEffect(() => {
        dispatch(
            fetchGetSocieties(token, page, limit, {
                dateInit: DateTime.now().minus({months: 1}).toFormat('yyyy-MM-dd'),
                dateEnd: DateTime.now().toFormat('yyyy-MM-dd'),
                status: 'ALL',
            })
        );
    }, [token]);

    return (
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Box
                sx={{
                    display: 'flex',
                    marginBottom: '20px',
                    padding: '15px',
                    height: '73px',
                    background: '#fff',
                    justifyContent: 'space-between',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        gap: '10px',
                    }}
                >
                    {UserType.INTERNAL_USER === user.type && (
                        <Controller
                            name="supplier"
                            control={control}
                            render={({field: {onChange, value}}) => {
                                return (
                                    <TextField
                                        value={value}
                                        onChange={onChange}
                                        label={t('supplier')}
                                        size="small"
                                    />
                                );
                            }}
                        />
                    )}
                    <Controller
                        name="societyName"
                        control={control}
                        render={({field}) => (
                            <TextField
                                {...field}
                                id="id"
                                label={`${t('billingStatement.filters.society')}`}
                                variant="outlined"
                                size="small"
                            />
                        )}
                        defaultValue={''}
                    />
                    <Controller
                        name="status"
                        control={control}
                        render={({field}) => (
                            <FormControl size="small" sx={{minWidth: 200}}>
                                <InputLabel>{t('billingStatement.filters.status')}</InputLabel>
                                <Select
                                    {...field}
                                    label={t('billingStatement.filters.status')}
                                    sx={{
                                        height: '40px',
                                    }}
                                >
                                    <MenuItem value="ALL">
                                        {t('billingStatement.filters.statusOptions.all')}
                                    </MenuItem>
                                    <MenuItem value="SHD">
                                        {t('billingStatement.filters.statusOptions.scheduled')}
                                    </MenuItem>
                                    <MenuItem value="PAY">
                                        {t('billingStatement.filters.statusOptions.paid')}
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        )}
                        defaultValue="ALL"
                    />
                    <LocalizationProvider dateAdapter={AdapterDateFns} locale={lang}>
                        <Controller
                            name="dateInit"
                            control={control}
                            defaultValue={DateTime.now().minus({months: 1}).toJSDate()}
                            render={({field}) => (
                                <DatePicker
                                    label={t('logbook_and_details.start_date')}
                                    value={field.value}
                                    onChange={(e) => {
                                        field.onChange(e);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            sx={{
                                                '& .MuiInputBase-root': {
                                                    height: '40px',
                                                },
                                            }}
                                        />
                                    )}
                                />
                            )}
                        />
                    </LocalizationProvider>
                    <LocalizationProvider dateAdapter={AdapterDateFns} locale={lang}>
                        <Controller
                            name="dateEnd"
                            control={control}
                            defaultValue={DateTime.now().toJSDate()}
                            render={({field}) => (
                                <DatePicker
                                    label={t('logbook_and_details.end_date')}
                                    value={field.value}
                                    onChange={(e) => {
                                        field.onChange(e);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            sx={{
                                                '& .MuiInputBase-root': {
                                                    height: '40px',
                                                },
                                            }}
                                        />
                                    )}
                                />
                            )}
                        />
                    </LocalizationProvider>
                    <Tooltip title={t('components.filter_btn.title')}>
                        <LoadingButton
                            variant="outlined"
                            loading={false}
                            type="submit"
                            color="secondary"
                            sx={{
                                minWidth: '40px',
                                height: '40px',
                                padding: '5px 5px',
                            }}
                        >
                            <Search />
                        </LoadingButton>
                    </Tooltip>
                </Box>
            </Box>
        </Box>
    );
};
